import type { LoginCredentials } from 'pollworker/src/components/Registration';
import { convertObjectKeysToCamelCase } from 'shared/src/utils/converters';
import { getWebsiteUserFromLocalStorage } from '../utils/localStorage';

export function fetchApi(uri: string, args: RequestInit, enforceAuth = true) {
  return fetch(`${import.meta.env.VITE_DOMAIN}${uri}`, {
    method: 'GET',
    ...args,
    headers: {
      ...getHeaders(),
      ...args.headers,
    },
  }).then((request) => {
    if (enforceAuth && request.status === 401) {
      window.localStorage.clear();
      window.location.href = '/login';
    }

    return request;
  });
}

// Date strings are formatted: /Date(epochTimestamp)/
export function mapDateString(dateString: string | null): string | null {
  if (dateString === null) {
    return null;
  }
  return dateString.replace(/[^0-9]/g, '');
}

export * from './featureFlags';
export * from './lms';
export * from './schedules';
export * from './timeclock';

function getDomain() {
  const domain = window.location.host.split('.').shift();
  return domain?.includes('localhost') || domain?.match(/\d{2}/g) ? 'localhost' : domain;
}

export function getHeaders(): HeadersInit {
  const websiteUserData = getWebsiteUserFromLocalStorage();

  return {
    'Easy-Vote-Authenticated-User': `UserId:${websiteUserData.UserId}|CustomerId:${websiteUserData.CustomerId}|ZumoToken:${window.localStorage.getItem('ZumoToken')}`,
    'ZUMO-API-VERSION': '2.0.0',
    'Content-Type': 'application/json',
  };
}

export function getHeadersFormData(): HeadersInit {
  const websiteUserData = getWebsiteUserFromLocalStorage();

  return {
    'Easy-Vote-Authenticated-User': `UserId:${websiteUserData.UserId}|CustomerId:${websiteUserData.CustomerId}|ZumoToken:${window.localStorage.getItem('ZumoToken')}`,
    'ZUMO-API-VERSION': '2.0.0',
  };
}

export function getHeadersDownload(): HeadersInit {
  return {
    ...getHeaders(),
    'Content-Type': 'application/octet-stream',
  };
}

export async function getPoliticalParties() {
  const { CustomerId: customerId } = getWebsiteUserFromLocalStorage();

  return await fetchApi(
    `/customer/politicalparties/${customerId}`,
    {
      method: 'GET',
      headers: new Headers({
        'Content-Type': 'application/json',
      }),
    },
    false,
  ).then((resp) => resp.json());
}

export async function updateUserProfile(evUserId: string, userProfile: PollworkerUserProfileFromAPI) {
  return fetchApi(`/user/profile/${evUserId}/save`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(userProfile),
  }).then((resp) => resp.json());
}

export async function getUserProfile(evUserId: string) {
  return fetchApi(`/user/profile/${evUserId}`, {
    method: 'GET',
    headers: getHeaders(),
  }).then((resp) => resp.json());
}

export async function startPasswordReset(userId: string) {
  return await fetchApi('/user/resetpassword', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ UserId: userId }),
  }).then((resp) => resp.json());
}

export async function performPasswordReset(resetCode: string, newPassword: string, userId: string) {
  return await fetchApi('/user/setnewpassword', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      NewPassword: newPassword,
      ResetCode: resetCode,
      UserId: userId,
    }),
  }).then((resp) => resp.json());
}

export async function getWebsiteUser() {
  return await fetchApi(`/authentication/getwebsiteuser/${getDomain()}`, {
    headers: {
      'Content-Type': 'application/json',
    },
  }).then((resp) => resp.json());
}

export async function getCurrentCustomer() {
  const resp = await fetchApi('/user/current', {
    headers: getHeaders(),
  });

  if (resp.status === 401) {
    return null;
  }

  let user = await resp.json();
  user = fixCurrentUserNomenclature(user);

  return user;
}

export async function getCurrentUser(evUserId: string) {
  const websiteUserData = getWebsiteUserFromLocalStorage();

  const resp = await fetchApi('/user/current', {
    headers: {
      'Easy-Vote-Authenticated-User': `UserId:${evUserId}|CustomerId:${websiteUserData.CustomerId}|ZumoToken:${window.localStorage.getItem('ZumoToken')}`,
    },
  });

  if (resp.status === 401) {
    return null;
  }

  let user = await resp.json();
  user = fixCurrentUserNomenclature(user);

  return user;
}

// Fixing the nomenclature keys because we have to match the admin app,
// because we're using stupid MVC and the first character is incorrectly capitalized
// biome-ignore lint/suspicious/noExplicitAny: messing with keys, trust me bro
function fixCurrentUserNomenclature(user: any) {
  const features = user?.Features;

  if (!user?.Customer?.CustomerNomenclature) {
    return user ? { ...user, features } : { features };
  }

  return {
    ...user,
    features,
    customer: {
      ...user.Customer,
      nomenclature: convertObjectKeysToCamelCase(user.Customer.CustomerNomenclature),
    },
  };
}

export function login(username: string, password: string): Promise<any> {
  return fetchApi('/authentication/loginregistereduser?timezoneoffset=-5', {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Basic ${btoa(`${username}:${password}`)}`,
    },
  }).then(async (resp) => [resp.ok, await resp.json()]);
}

export function validateFormData(data: PollworkerProfilePayload) {
  const websiteUser = getWebsiteUserFromLocalStorage();

  return fetchApi(`/user/${websiteUser.CustomerId}/validateapplicationdata`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(data),
  }).then((resp) => resp.json());
}

export function submitModernFormApplication(data: PollworkerProfilePayload) {
  const websiteUser = getWebsiteUserFromLocalStorage();

  return fetchApi(`/user/${websiteUser.CustomerId}/apply/dynamic`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(data),
  }).then((resp) => resp.json());
}

export function submitApplication(data: PollworkerProfilePayload) {
  const websiteUser = getWebsiteUserFromLocalStorage();

  return fetchApi(`/user/${websiteUser.CustomerId}/apply`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(data),
  }).then((resp) => resp.json());
}

export async function getPollworkerApplication(formId: string | null) {
  const { CustomerId } = getWebsiteUserFromLocalStorage();
  let path = `/user/${CustomerId}/applicationdata`;
  if (formId) path += `?formId=${formId}`;

  return fetchApi(path, {
    method: 'GET',
    headers: getHeaders(),
  }).then((resp) => resp.json());
}

export async function uploadDocument(evUserId: string, customerId: string, filename: string, file: File) {
  let success = false;

  const uploadFormData: FormData = new FormData();
  uploadFormData.append('uploadFile', file, file.name);

  const tempFileName = await fetch(`${import.meta.env.VITE_DOMAIN}/documents/uploaddocument`, {
    method: 'POST',
    headers: getHeadersFormData(),
    body: uploadFormData,
  }).then((resp) => resp.json());

  if (tempFileName) {
    success = await fetchApi('/documents/add', {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({
        DocumentName: filename,
        TempFileName: tempFileName,
        EVUserId: evUserId,
        CustomerId: customerId,
      }),
    }).then((resp) => resp.json());
  }

  return [success, tempFileName];
}

export async function getElections() {
  const resp = await fetchApi('/customer/elections/getall', {
    headers: getHeaders(),
  });

  if (resp.status === 401) {
    return [];
  }

  return await resp.json();
}

export async function getRequiredClasses(electionId: string) {
  const resp = await fetchApi(`/requiredclasses/customer/${electionId}/user`, {
    headers: getHeaders(),
  });
  return await resp.json();
}

export async function getRequiredClassSessions(electionId: string, classId: string) {
  return fetchApi(`/requiredclasses/sessions/${electionId}/${classId}`, {
    headers: getHeaders(),
  }).then(async (resp) => await resp.json());
}

export async function getScheduledClasses(electionId: string) {
  return fetchApi(`/scheduledclasses/customer/${electionId}/user`, {
    headers: getHeaders(),
  }).then(async (resp) => await resp.json());
}

export async function getScheduledClassDetail(scheduledClassId: string) {
  return fetchApi(`/scheduledclasses/sessiondetail/${scheduledClassId}`, {
    headers: getHeaders(),
  }).then(async (resp) => await resp.json());
}

export async function signUpForClassSession(classSignup: ClassSessionPayload) {
  return fetchApi('/requiredclasses/signup', {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(classSignup),
  }).then(async (resp) => await resp.json());
}

export async function getClassDocumentBySessionId(sessionId: string, evUserId: string) {
  // gets the class document for the current session from the server
  return fetchApi(`/scheduledclasses/${sessionId}/${evUserId}/sessionscheduledocumentbyuserid`, {
    headers: getHeadersDownload(),
  }).then(async (resp) => await resp.blob());
}

async function getResponseErrorMessage(resp: Response) {
  try {
    return await resp.text();
  } catch {
    return resp.statusText || 'An error occurred.';
  }
}

export async function dropClass(evUserId: string, sessionId: string) {
  const resp = await fetchApi('/scheduledclasses/dropclass', {
    method: 'POST',
    body: JSON.stringify({ EVUserId: evUserId, SessionId: sessionId }),
  });

  if (!resp.ok) {
    throw new Error(await getResponseErrorMessage(resp));
  }

  return await resp.json();
}

export async function getUserTrainingOverviewForStaffing(electionId: string) {
  const resp = await fetchApi(`/staffing/requiredclasses/customer/${electionId}/user/training`, {
    headers: getHeaders(),
  });
  return await resp.json();
}

export async function getScheduledClassDetailForStaffing(scheduledClassId: string) {
  return fetchApi(`/staffing/scheduledclasses/sessiondetail/${scheduledClassId}`, {
    headers: getHeaders(),
  }).then(async (resp) => await resp.json());
}

export async function signUpForClassSessionForStaffing(classSignup: ClassSessionPayload) {
  return fetchApi('/staffing/requiredclasses/signup', {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(classSignup),
  }).then(async (resp) => await resp.json());
}

export async function getClassDocumentBySessionIdForStaffing(sessionId: string, evUserId: string) {
  return fetchApi(`/staffing/scheduledclasses/${sessionId}/${evUserId}/sessionscheduledocumentbyuserid`, {
    headers: getHeadersDownload(),
  }).then(async (resp) => await resp.blob());
}

export async function dropClassForStaffing(evUserId: string, sessionId: string) {
  const resp = await fetchApi('/staffing/scheduledclasses/dropclass', {
    method: 'POST',
    body: JSON.stringify({ EVUserId: evUserId, SessionId: sessionId }),
  });

  if (!resp.ok) {
    throw new Error(await getResponseErrorMessage(resp));
  }

  return await resp.json();
}

export async function getNews(customerId: string) {
  return fetchApi(`/blogs/${customerId}`, {
    headers: getHeaders(),
  }).then(async (resp) => await resp.json());
}

export async function getAvailabilitySurveyWorkDates(scheduleSurveyId: string, evUserId: string) {
  return fetchApi(`/survey/user/${scheduleSurveyId}/${evUserId}`, {
    headers: getHeaders(),
  }).then((resp) => resp.json());
}

export async function saveAvailabilitySurveyWorkDates(scheduleSurveyId: string, evUserId: string, dateSurveyed: string, isAvailable: boolean) {
  return fetchApi(`/survey/user/${scheduleSurveyId}/${evUserId}/saveresult`, {
    headers: getHeaders(),
    method: 'POST',
    body: JSON.stringify({ dateSurveyed, isAvailable }),
  }).then((resp) => resp.json());
}

export async function getDocumentFile(documentId: string) {
  return fetchApi(`/documents/getfromsecureblobstorage/${documentId}`, {
    headers: getHeadersDownload(),
  });
}

export async function getWorkSchedule(electionId: string) {
  return fetchApi(`/workschedules/${electionId}/user`, {
    headers: getHeaders(),
  }).then(async (resp) => await resp.json());
}

export async function getScheduledCoworkers(scheduleId: string) {
  return fetchApi(`/workschedules/scheduledcoworkers/${scheduleId}`, {
    headers: getHeaders(),
  }).then((resp) => resp.json());
}

export async function getWorkScheduleForEditing(scheduleId: string) {
  return fetchApi(`/workschedules/editworkschedule/${scheduleId}`, {
    headers: getHeaders(),
  }).then(async (resp) => await resp.json());
}

export async function editWorkSchedule(workSchedule: any) {
  return fetchApi('/workschedules/edit', {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(workSchedule),
  }).then(async (resp) => await resp.json());
}

export async function getListOfDocuments() {
  return fetchApi('/documents', {
    headers: getHeaders(),
  }).then(async (resp) => await resp.json());
}

export async function getEmployeeImage(customerId: string, userId: string) {
  // TODO: make this work
  // gets the employee image for the current user from the server
  return await fetchApi(`/images/${customerId}/${userId}/employeeimage`, {
    headers: getHeaders(),
  }).then(async (resp) => {
    if (!resp.ok) return Promise.reject();
    return await resp.blob();
  });
}

export async function verifyCreateCredentials(evUserId: string, verificationInfo: any) {
  return fetchApi(`/user/${evUserId}/verifycreatecredentials`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(verificationInfo),
  }).then(async (resp) => await resp.json());
}

export async function verifyUserIdUnique(userId: string) {
  return fetchApi('/user/verifyuseridunique', {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ UserName: userId }),
  }).then(async (resp) => await resp.json());
}

export async function updateLoginCredentials(evUserId: string, loginCredentials: LoginCredentials) {
  return fetchApi(`/user/${evUserId}/updatelogincredentials`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(loginCredentials),
  }).then(async (resp) => {
    if (!resp.ok) {
      if (resp.status === 410) {
        throw new Error('The registration link is no longer valid.');
      }
      throw new Error(`Failed to update login credentials: ${resp.status}`);
    }
    return await resp.json();
  });
}
